// client/src/components/tendermaker/PdfTextOverlay.js
import React, { useCallback, useRef, useState } from "react";
import { tmAddTextBoxes } from "./tendermakerApi";

const btn = {
  padding: "5px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 11,
};
const btn2 = { ...btn, background: "#fafafa" };
const btnPrimary = { ...btn, background: "#2563eb", color: "#fff", borderColor: "#2563eb" };

let nextId = 1;

export default function PdfTextOverlay({
  pdfUrl,
  runId,
  activeItemId,
  activePdfKey,
  onSetErr,
  onPdfUpdated,
}) {
  const [textBoxes, setTextBoxes] = useState([]); // { id, x, y, text, fontSize, color, page }
  const [dragging, setDragging] = useState(null); // { id, offsetX, offsetY }
  const [busy, setBusy] = useState(false);
  const containerRef = useRef(null);

  function addTextBox() {
    const id = nextId++;
    setTextBoxes((prev) => [
      ...prev,
      { id, x: 50, y: 50, text: "", fontSize: 12, color: "#000000", page: 1 },
    ]);
  }

  function updateBox(id, updates) {
    setTextBoxes((prev) =>
      prev.map((b) => (b.id === id ? { ...b, ...updates } : b))
    );
  }

  function removeBox(id) {
    setTextBoxes((prev) => prev.filter((b) => b.id !== id));
  }

  const handleMouseDown = useCallback((e, boxId) => {
    e.preventDefault();
    const box = e.currentTarget;
    const rect = box.getBoundingClientRect();
    setDragging({ id: boxId, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top });
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!dragging || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, e.clientX - rect.left - dragging.offsetX);
      const y = Math.max(0, e.clientY - rect.top - dragging.offsetY);
      updateBox(dragging.id, { x, y });
    },
    [dragging]
  );

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  async function applyTextBoxes() {
    if (!activePdfKey || !textBoxes.length) {
      onSetErr("No text boxes to apply or no PDF key.");
      return;
    }
    setBusy(true);
    try {
      // Convert pixel positions to approximate PDF points
      // Assume container roughly maps to a standard A4 page (595 x 842 pt)
      const containerEl = containerRef.current;
      const cW = containerEl?.offsetWidth || 600;
      const cH = containerEl?.offsetHeight || 800;

      const boxes = textBoxes.map((b) => ({
        page: b.page,
        x: (b.x / cW) * 595,
        y: 842 - (b.y / cH) * 842, // PDF y is bottom-up
        text: b.text,
        fontSize: b.fontSize,
        color: b.color,
      }));

      const result = await tmAddTextBoxes({
        key: activePdfKey,
        runId,
        textBoxes: boxes,
      });

      if (result?.url) {
        onPdfUpdated(result.url);
        setTextBoxes([]);
      }
    } catch (e) {
      onSetErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, height: "100%", minHeight: 0, overflow: "hidden" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
        <button style={btn2} onClick={addTextBox}>
          + Add Text Box
        </button>
        {textBoxes.length > 0 && (
          <>
            <button
              style={btnPrimary}
              onClick={applyTextBoxes}
              disabled={busy || !activePdfKey}
            >
              {busy ? "Applying..." : `Apply ${textBoxes.length} Text Box${textBoxes.length > 1 ? "es" : ""}`}
            </button>
            <button
              style={{ ...btn, color: "#dc2626", borderColor: "#fca5a5" }}
              onClick={() => setTextBoxes([])}
            >
              Clear All
            </button>
          </>
        )}
        <span style={{ fontSize: 10, color: "#9ca3af", marginLeft: "auto" }}>
          Drag text boxes to position on the PDF
        </span>
      </div>

      {/* Text box properties */}
      {textBoxes.length > 0 && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", maxHeight: 80, overflowY: "auto" }}>
          {textBoxes.map((b) => (
            <div
              key={b.id}
              style={{
                display: "flex",
                gap: 4,
                alignItems: "center",
                padding: "3px 6px",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                background: "#fff",
                fontSize: 10,
              }}
            >
              <input
                value={b.text}
                onChange={(e) => updateBox(b.id, { text: e.target.value })}
                placeholder="Text..."
                style={{ width: 100, fontSize: 10, padding: "2px 4px", border: "1px solid #e5e7eb", borderRadius: 4 }}
              />
              <input
                type="number"
                value={b.fontSize}
                onChange={(e) => updateBox(b.id, { fontSize: Number(e.target.value) || 12 })}
                style={{ width: 35, fontSize: 10, padding: "2px 2px", border: "1px solid #e5e7eb", borderRadius: 4 }}
                title="Font size"
              />
              <input
                type="number"
                value={b.page}
                onChange={(e) => updateBox(b.id, { page: Math.max(1, Number(e.target.value) || 1) })}
                style={{ width: 30, fontSize: 10, padding: "2px 2px", border: "1px solid #e5e7eb", borderRadius: 4 }}
                title="Page number"
                min={1}
              />
              <input
                type="color"
                value={b.color}
                onChange={(e) => updateBox(b.id, { color: e.target.value })}
                style={{ width: 20, height: 18, padding: 0, border: "none", cursor: "pointer" }}
              />
              <button
                style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontWeight: 700, fontSize: 12 }}
                onClick={() => removeBox(b.id)}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {/* PDF viewer with overlay layer */}
      <div
        ref={containerRef}
        style={{ flex: 1, minHeight: 0, position: "relative", overflow: "hidden", borderRadius: 8 }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <iframe
          src={pdfUrl}
          style={{ width: "100%", height: "100%", border: "1px solid #e5e7eb", borderRadius: 8 }}
          title="PDF Editor"
        />

        {/* Draggable text boxes overlay */}
        {textBoxes.map((b) => (
          <div
            key={b.id}
            onMouseDown={(e) => handleMouseDown(e, b.id)}
            style={{
              position: "absolute",
              left: b.x,
              top: b.y,
              padding: "2px 6px",
              border: "2px dashed #2563eb",
              borderRadius: 4,
              background: "rgba(255,255,255,0.85)",
              cursor: dragging?.id === b.id ? "grabbing" : "grab",
              fontSize: b.fontSize,
              color: b.color,
              fontWeight: 600,
              minWidth: 30,
              minHeight: 18,
              userSelect: "none",
              zIndex: 10,
              whiteSpace: "nowrap",
            }}
          >
            {b.text || "(empty)"}
          </div>
        ))}
      </div>
    </div>
  );
}
